import { useState, useEffect, useCallback, useMemo } from 'react'
import { getNexusClient } from '@/lib/supabase/client'
import { useAuth } from '@/lib/auth/context'
import {
  TeamAvailability,
  TeamAvailabilityRow,
  AvailabilityOverride,
  AvailabilityOverrideRow,
  DayAvailability,
  TimeSlot,
  CreateAvailabilityInput,
  CreateOverrideInput,
  UseAvailableSlotsOptions,
  CalendarEventRow,
  availabilityRowToAvailability,
} from '@/types/calendar'

// ============================================================================
// useTeamAvailability - Get/manage base availability hours
// ============================================================================

export function useTeamAvailability(userId?: string) {
  const { adminUser } = useAuth()
  const targetUserId = userId || adminUser?.id

  const [availability, setAvailability] = useState<TeamAvailability[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchAvailability = useCallback(async () => {
    if (!targetUserId) {
      setAvailability([])
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      const { data, error: fetchError } = await client
        .from('team_availability')
        .select('*')
        .eq('admin_user_id', targetUserId)
        .order('day_of_week', { ascending: true })
        .order('start_time', { ascending: true })
        .returns<TeamAvailabilityRow[]>()

      if (fetchError) throw fetchError

      setAvailability((data || []).map(row => availabilityRowToAvailability(row)))
    } catch (err: any) {
      console.error('Error fetching team availability:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [targetUserId])

  useEffect(() => {
    fetchAvailability()
  }, [fetchAvailability])

  return {
    availability,
    isLoading,
    error,
    refetch: fetchAvailability,
  }
}

// ============================================================================
// useAvailabilityOverrides - Get date-specific overrides
// ============================================================================

export function useAvailabilityOverrides(
  userId: string | undefined,
  startDate: string,
  endDate: string
) {
  const [overrides, setOverrides] = useState<AvailabilityOverride[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchOverrides = useCallback(async () => {
    if (!userId) {
      setOverrides([])
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      const { data, error: fetchError } = await client
        .from('availability_overrides')
        .select('*')
        .eq('admin_user_id', userId)
        .gte('override_date', startDate)
        .lte('override_date', endDate)
        .order('override_date', { ascending: true })
        .returns<AvailabilityOverrideRow[]>()

      if (fetchError) throw fetchError

      setOverrides((data || []).map(row => ({
        id: row.id,
        adminUserId: row.admin_user_id,
        overrideDate: row.override_date,
        overrideType: row.override_type,
        startTime: row.start_time ?? undefined,
        endTime: row.end_time ?? undefined,
        reason: row.reason ?? undefined,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      })))
    } catch (err: any) {
      console.error('Error fetching availability overrides:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [userId, startDate, endDate])

  useEffect(() => {
    fetchOverrides()
  }, [fetchOverrides])

  return {
    overrides,
    isLoading,
    error,
    refetch: fetchOverrides,
  }
}

// ============================================================================
// useAvailabilityMutations - Create/update/delete availability
// ============================================================================

export function useAvailabilityMutations() {
  const { adminUser } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Set availability for a day of week
  // Note: We delete-then-insert because the UI only supports one time range per day,
  // but the DB constraint is on (admin_user_id, day_of_week, start_time)
  const setAvailability = useCallback(async (
    input: CreateAvailabilityInput
  ): Promise<TeamAvailability | null> => {
    if (!adminUser?.id) {
      setError('You must be logged in to set availability')
      return null
    }

    setIsLoading(true)
    setError(null)

    console.log('📅 setAvailability called with:', input)

    try {
      const client = getNexusClient()

      // First, delete any existing availability for this day
      // This handles the case where start_time changed (upsert won't match old record)
      const { error: deleteError } = await client
        .from('team_availability')
        .delete()
        .eq('admin_user_id', adminUser.id)
        .eq('day_of_week', input.dayOfWeek)

      if (deleteError) {
        console.error('📅 Delete error:', deleteError)
      } else {
        console.log('📅 Deleted existing records for day', input.dayOfWeek)
      }

      // Then insert the new availability
      const insertPayload = {
        admin_user_id: adminUser.id,
        day_of_week: input.dayOfWeek,
        start_time: input.startTime,
        end_time: input.endTime,
        is_available: input.isAvailable ?? true,
        timezone: input.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      }
      console.log('📅 Inserting:', insertPayload)

      const { data, error: insertError } = await (client as any)
        .from('team_availability')
        .insert(insertPayload)
        .select()
        .single()

      if (insertError) {
        console.error('📅 Insert error:', insertError)
        throw insertError
      }

      console.log('📅 Insert successful:', data)
      return data ? availabilityRowToAvailability(data) : null
    } catch (err: any) {
      console.error('📅 Error setting availability:', err)
      setError(err.message)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [adminUser])

  // Set default weekly availability (Mon-Fri 9-5)
  const setDefaultAvailability = useCallback(async (
    options: { startTime?: string; endTime?: string; timezone?: string } = {}
  ): Promise<boolean> => {
    if (!adminUser?.id) {
      setError('You must be logged in to set availability')
      return false
    }

    setIsLoading(true)
    setError(null)

    const { startTime = '09:00', endTime = '17:00', timezone } = options
    const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone

    try {
      const client = getNexusClient()

      // Create availability for Monday (1) through Friday (5)
      const availabilityRows = [1, 2, 3, 4, 5].map(day => ({
        admin_user_id: adminUser.id,
        day_of_week: day,
        start_time: startTime,
        end_time: endTime,
        is_available: true,
        timezone: tz,
      }))

      const { error: upsertError } = await (client as any)
        .from('team_availability')
        .upsert(availabilityRows, {
          onConflict: 'admin_user_id,day_of_week,start_time',
        })

      if (upsertError) throw upsertError

      return true
    } catch (err: any) {
      console.error('Error setting default availability:', err)
      setError(err.message)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [adminUser])

  // Remove availability for a day
  const removeAvailability = useCallback(async (
    dayOfWeek: number,
    startTime?: string
  ): Promise<boolean> => {
    if (!adminUser?.id) {
      setError('You must be logged in to remove availability')
      return false
    }

    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      let query = client
        .from('team_availability')
        .delete()
        .eq('admin_user_id', adminUser.id)
        .eq('day_of_week', dayOfWeek)

      if (startTime) {
        query = query.eq('start_time', startTime)
      }

      const { error: deleteError } = await query

      if (deleteError) throw deleteError

      return true
    } catch (err: any) {
      console.error('Error removing availability:', err)
      setError(err.message)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [adminUser])

  // Add override for a specific date
  const addOverride = useCallback(async (
    input: CreateOverrideInput
  ): Promise<AvailabilityOverride | null> => {
    if (!adminUser?.id) {
      setError('You must be logged in to add overrides')
      return null
    }

    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      const { data, error: insertError } = await (client as any)
        .from('availability_overrides')
        .insert({
          admin_user_id: adminUser.id,
          override_date: input.overrideDate,
          override_type: input.overrideType,
          start_time: input.startTime || null,
          end_time: input.endTime || null,
          reason: input.reason || null,
        })
        .select()
        .single()

      if (insertError) throw insertError

      const row = data as AvailabilityOverrideRow | null
      return row ? {
        id: row.id,
        adminUserId: row.admin_user_id,
        overrideDate: row.override_date,
        overrideType: row.override_type,
        startTime: row.start_time ?? undefined,
        endTime: row.end_time ?? undefined,
        reason: row.reason ?? undefined,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      } : null
    } catch (err: any) {
      console.error('Error adding override:', err)
      setError(err.message)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [adminUser])

  // Remove override
  const removeOverride = useCallback(async (overrideId: string): Promise<boolean> => {
    if (!adminUser?.id) {
      setError('You must be logged in to remove overrides')
      return false
    }

    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      const { error: deleteError } = await client
        .from('availability_overrides')
        .delete()
        .eq('id', overrideId)
        .eq('admin_user_id', adminUser.id)

      if (deleteError) throw deleteError

      return true
    } catch (err: any) {
      console.error('Error removing override:', err)
      setError(err.message)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [adminUser])

  return {
    setAvailability,
    setDefaultAvailability,
    removeAvailability,
    addOverride,
    removeOverride,
    isLoading,
    error,
  }
}

// ============================================================================
// useAvailableSlots - Calculate available time slots for a date
// ============================================================================

export function useAvailableSlots(options: UseAvailableSlotsOptions) {
  const { userId, date, duration = 30 } = options

  const [slots, setSlots] = useState<TimeSlot[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const calculateSlots = useCallback(async () => {
    if (!userId || !date) {
      setSlots([])
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()
      const targetDate = new Date(date)
      const dayOfWeek = targetDate.getDay() // 0-6

      // Get base availability for this day
      const { data: availabilityRows } = await client
        .from('team_availability')
        .select('*')
        .eq('admin_user_id', userId)
        .eq('day_of_week', dayOfWeek)
        .eq('is_available', true)
        .returns<TeamAvailabilityRow[]>()

      // Check for date-specific override
      const { data: overrideRows } = await client
        .from('availability_overrides')
        .select('*')
        .eq('admin_user_id', userId)
        .eq('override_date', date)
        .returns<AvailabilityOverrideRow[]>()

      // Determine available time ranges
      let availableRanges: { start: string; end: string }[] = []

      // Check if there's an "unavailable" override
      const unavailableOverride = overrideRows?.find(o => o.override_type === 'unavailable')
      if (unavailableOverride) {
        // User is unavailable this entire day
        setSlots([])
        return
      }

      // Check for custom override
      const customOverrides = overrideRows?.filter(o => o.override_type === 'custom')
      if (customOverrides && customOverrides.length > 0) {
        // Use custom hours instead of base availability
        availableRanges = customOverrides.map(o => ({
          start: o.start_time!,
          end: o.end_time!,
        }))
      } else if (availabilityRows && availabilityRows.length > 0) {
        // Use base availability
        availableRanges = availabilityRows.map(a => ({
          start: a.start_time,
          end: a.end_time,
        }))
      } else {
        // No availability set
        setSlots([])
        return
      }

      // Get existing events for this date
      const startOfDay = new Date(date)
      startOfDay.setHours(0, 0, 0, 0)
      const endOfDay = new Date(date)
      endOfDay.setHours(23, 59, 59, 999)

      const { data: eventRows } = await client
        .from('calendar_events')
        .select('start_time, end_time')
        .eq('owner_id', userId)
        .gte('start_time', startOfDay.toISOString())
        .lte('end_time', endOfDay.toISOString())
        .returns<Pick<CalendarEventRow, 'start_time' | 'end_time'>[]>()

      const existingEvents = (eventRows || []).map(e => ({
        start: new Date(e.start_time),
        end: new Date(e.end_time),
      }))

      // Generate slots
      const allSlots: TimeSlot[] = []

      for (const range of availableRanges) {
        const [startHour, startMin] = range.start.split(':').map(Number)
        const [endHour, endMin] = range.end.split(':').map(Number)

        const rangeStart = new Date(date)
        rangeStart.setHours(startHour, startMin, 0, 0)

        const rangeEnd = new Date(date)
        rangeEnd.setHours(endHour, endMin, 0, 0)

        // Generate slots within this range
        let slotStart = new Date(rangeStart)

        while (slotStart < rangeEnd) {
          const slotEnd = new Date(slotStart.getTime() + duration * 60 * 1000)

          if (slotEnd > rangeEnd) break

          // Check if slot conflicts with existing events
          const isConflicting = existingEvents.some(event =>
            (slotStart < event.end && slotEnd > event.start)
          )

          allSlots.push({
            start: slotStart.toISOString(),
            end: slotEnd.toISOString(),
            available: !isConflicting,
          })

          // Move to next slot
          slotStart = new Date(slotStart.getTime() + duration * 60 * 1000)
        }
      }

      setSlots(allSlots)
    } catch (err: any) {
      console.error('Error calculating available slots:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [userId, date, duration])

  useEffect(() => {
    calculateSlots()
  }, [calculateSlots])

  // Filter to only available slots
  const availableSlots = useMemo(() =>
    slots.filter(s => s.available),
    [slots]
  )

  return {
    slots,
    availableSlots,
    isLoading,
    error,
    refetch: calculateSlots,
  }
}

// ============================================================================
// useAvailabilityForDateRange - Get availability for multiple dates
// ============================================================================

export function useAvailabilityForDateRange(
  userId: string | undefined,
  startDate: string,
  endDate: string,
  duration = 30
) {
  const [dayAvailability, setDayAvailability] = useState<DayAvailability[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const calculateRange = useCallback(async () => {
    if (!userId || !startDate || !endDate) {
      setDayAvailability([])
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      // Get base availability
      const { data: availabilityRows } = await client
        .from('team_availability')
        .select('*')
        .eq('admin_user_id', userId)
        .eq('is_available', true)
        .returns<TeamAvailabilityRow[]>()

      // Get overrides for date range
      const { data: overrideRows } = await client
        .from('availability_overrides')
        .select('*')
        .eq('admin_user_id', userId)
        .gte('override_date', startDate)
        .lte('override_date', endDate)
        .returns<AvailabilityOverrideRow[]>()

      // Get events for date range
      const { data: eventRows } = await client
        .from('calendar_events')
        .select('start_time, end_time')
        .eq('owner_id', userId)
        .gte('start_time', new Date(startDate).toISOString())
        .lte('end_time', new Date(endDate + 'T23:59:59').toISOString())
        .returns<Pick<CalendarEventRow, 'start_time' | 'end_time'>[]>()

      // Group availability by day of week
      const availabilityByDay = (availabilityRows || []).reduce((acc, row) => {
        if (!acc[row.day_of_week]) acc[row.day_of_week] = []
        acc[row.day_of_week].push(row)
        return acc
      }, {} as Record<number, TeamAvailabilityRow[]>)

      // Group overrides by date
      const overridesByDate = (overrideRows || []).reduce((acc, row) => {
        if (!acc[row.override_date]) acc[row.override_date] = []
        acc[row.override_date].push(row)
        return acc
      }, {} as Record<string, AvailabilityOverrideRow[]>)

      // Process each date in range
      const result: DayAvailability[] = []
      // Parse dates as local time, not UTC (YYYY-MM-DD strings are parsed as UTC by default)
      const [startY, startM, startD] = startDate.split('-').map(Number)
      const [endY, endM, endD] = endDate.split('-').map(Number)
      const current = new Date(startY, startM - 1, startD)
      const end = new Date(endY, endM - 1, endD)

      while (current <= end) {
        // Use local date format to avoid UTC date shift issues
        const y = current.getFullYear()
        const m = String(current.getMonth() + 1).padStart(2, '0')
        const d = String(current.getDate()).padStart(2, '0')
        const dateStr = `${y}-${m}-${d}`
        const dayOfWeek = current.getDay()

        const dayOverrides = overridesByDate[dateStr] || []
        const unavailableOverride = dayOverrides.find(o => o.override_type === 'unavailable')

        if (unavailableOverride) {
          result.push({
            date: dateStr,
            isAvailable: false,
            slots: [],
            override: {
              id: unavailableOverride.id,
              adminUserId: unavailableOverride.admin_user_id,
              overrideDate: unavailableOverride.override_date,
              overrideType: unavailableOverride.override_type,
              reason: unavailableOverride.reason ?? undefined,
              createdAt: unavailableOverride.created_at,
              updatedAt: unavailableOverride.updated_at,
            },
          })
        } else {
          // Calculate slots for this day
          const customOverrides = dayOverrides.filter(o => o.override_type === 'custom')
          const baseAvailability = availabilityByDay[dayOfWeek] || []

          const ranges = customOverrides.length > 0
            ? customOverrides.map(o => ({ start: o.start_time!, end: o.end_time! }))
            : baseAvailability.map(a => ({ start: a.start_time, end: a.end_time }))

          const dayEvents = (eventRows || [])
            .filter(e => {
              // Use local date format for comparison
              const eventDt = new Date(e.start_time)
              const eventY = eventDt.getFullYear()
              const eventM = String(eventDt.getMonth() + 1).padStart(2, '0')
              const eventD = String(eventDt.getDate()).padStart(2, '0')
              const eventDate = `${eventY}-${eventM}-${eventD}`
              return eventDate === dateStr
            })
            .map(e => ({
              start: new Date(e.start_time),
              end: new Date(e.end_time),
            }))

          const slots: TimeSlot[] = []

          for (const range of ranges) {
            const [startHour, startMin] = range.start.split(':').map(Number)
            const [endHour, endMin] = range.end.split(':').map(Number)

            const rangeStart = new Date(current)
            rangeStart.setHours(startHour, startMin, 0, 0)

            const rangeEnd = new Date(current)
            rangeEnd.setHours(endHour, endMin, 0, 0)

            let slotStart = new Date(rangeStart)

            while (slotStart < rangeEnd) {
              const slotEnd = new Date(slotStart.getTime() + duration * 60 * 1000)
              if (slotEnd > rangeEnd) break

              const isConflicting = dayEvents.some(event =>
                (slotStart < event.end && slotEnd > event.start)
              )

              slots.push({
                start: slotStart.toISOString(),
                end: slotEnd.toISOString(),
                available: !isConflicting,
              })

              slotStart = new Date(slotStart.getTime() + duration * 60 * 1000)
            }
          }

          result.push({
            date: dateStr,
            isAvailable: slots.some(s => s.available),
            slots,
          })
        }

        current.setDate(current.getDate() + 1)
      }

      setDayAvailability(result)
    } catch (err: any) {
      console.error('Error calculating availability range:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [userId, startDate, endDate, duration])

  useEffect(() => {
    calculateRange()
  }, [calculateRange])

  return {
    dayAvailability,
    isLoading,
    error,
    refetch: calculateRange,
  }
}

// Export types
export type { TeamAvailability, AvailabilityOverride, DayAvailability, TimeSlot }
